/**
 * Yazıcı adresi seçici — görev ekinin (ve ileride SP parametresinin) yazıcı alanı.
 *
 * Liste Cihazlar'daki ajan yazıcılarından gelir (takma ad ya da CIHAZ/Yazıcı). Listede
 * olmayan bir ad da yazılabilir: o zaman sunucu yazıcısıdır, Worker kendi makinesinde
 * basar (eski davranış). Altındaki satır seçimin NEREDE basılacağını söyler — kullanıcı
 * "yazıcı adını yazdım ama depoda çıkmadı" sorusunu kaydetmeden önce görsün.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Pencil, Printer, Server } from 'lucide-react';
import { Input } from '@/shared/ui/Input';
import { IconButton } from '@/shared/ui/IconButton';
import { SearchSelect } from '@/shared/ui/SearchSelect';
import { queryKeys } from '@/shared/api/queryKeys';
import { cn } from '@/shared/lib/cn';
import { deviceApi } from '../api/deviceApi';
import { printerOptions, resolvePrinterAddress } from '../lib/printerOptions';

export function PrinterPicker({
  id,
  value,
  onChange,
  className,
}: {
  id?: string;
  value: string;
  onChange: (next: string) => void;
  className?: string;
}) {
  const { t } = useTranslation();
  const devices = useQuery({ queryKey: queryKeys.device.list(), queryFn: () => deviceApi.list(), staleTime: 30_000 });
  const options = printerOptions(devices.data, { offline: t('shell.user.devicesOffline') });
  const match = resolvePrinterAddress(value, devices.data);
  const trimmed = value.trim();
  const inList = options.some((o) => o.id === trimmed);
  // Elle yazılan (sunucu) ad listede yoksa seçili görünsün diye seçenek olarak eklenir.
  const allOptions = trimmed && !inList ? [{ id: trimmed, name: `${trimmed} · ${t('worker.printerServer')}` }, ...options] : options;
  // Liste hiç gelmediyse (ajan yok, uç hata) elle yazma zaten tek yol.
  const [manual, setManual] = useState(false);
  const useInput = manual || (!devices.isLoading && options.length === 0);

  return (
    <div className={cn('flex flex-col gap-1', className)}>
      <div className="flex items-center gap-1">
        {useInput ? (
          <Input id={id} value={value} onChange={(e) => onChange(e.target.value)} placeholder={t('worker.printerPlaceholder')} className="min-w-0 flex-1" />
        ) : (
          <SearchSelect
            id={id}
            value={trimmed || null}
            options={allOptions}
            placeholder={devices.isLoading ? t('worker.printerLoading') : t('worker.printerPick')}
            onChange={(v) => onChange(v ?? '')}
            className="min-w-0 flex-1"
          />
        )}
        {options.length > 0 ? (
          <IconButton label={useInput ? t('worker.printerPickList') : t('worker.printerManual')} size="sm" onClick={() => setManual((m) => !m)}>
            {useInput ? <Printer className="h-4 w-4" /> : <Pencil className="h-4 w-4" />}
          </IconButton>
        ) : null}
      </div>
      {/* Nerede basılacak: ajan (cihaz adı, çevrimdışıysa kuyruk uyarısı) ya da sunucu. */}
      {trimmed ? (
        match ? (
          <span className={cn('flex items-center gap-1 text-xs', match.device.online ? 'text-fg-muted' : 'text-warning')} data-testid="printer-route">
            <Printer className="h-3 w-3 shrink-0" />
            {t('worker.printerDeviceHint', { device: match.device.deviceCode, printer: match.printer.printerName })}
            {match.device.online ? '' : ` · ${t('worker.printerOffline')}`}
          </span>
        ) : trimmed.toLowerCase().startsWith('@local') || trimmed.toLowerCase().startsWith('@yerel') ? (
          <span className="text-warning flex items-center gap-1 text-xs" data-testid="printer-route">
            <Printer className="h-3 w-3 shrink-0" />
            {t('worker.printerLocalHint')}
          </span>
        ) : (
          <span className="text-fg-muted flex items-center gap-1 text-xs" data-testid="printer-route">
            <Server className="h-3 w-3 shrink-0" />
            {t('worker.printerServerHint')}
          </span>
        )
      ) : (
        <span className="text-fg-subtle text-xs">{t('worker.printerEmptyHint')}</span>
      )}
    </div>
  );
}
